
import { useState } from 'react';
import { GuestData, SelectedDate, TimeSlot } from '../types';
import { inviteService } from '@/services/inviteService';
// Import from our custom wrapper instead of directly from sonner
import { toast } from '@/utils/sonner-toast-wrapper';

interface UseRSVPSubmissionProps {
  eventId: string | null;
  selectedGuest: GuestData | null;
  phoneNumber: string;
  rsvpResponse: 'yes' | 'no' | null;
  selectedDates: SelectedDate[];
  selectedTimes: TimeSlot[];
  feedback: string;
  guestAvatarUrl?: string | null;
  isUpdate?: boolean; // Add isUpdate flag
}

export const useRSVPSubmission = ({
  eventId,
  selectedGuest,
  phoneNumber,
  rsvpResponse,
  selectedDates,
  selectedTimes,
  feedback,
  guestAvatarUrl,
  isUpdate = false // Default to false for backward compatibility
}: UseRSVPSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Modified to accept an optional direct feedback parameter
  const submitRSVP = async (directFeedback?: string): Promise<boolean> => {
    if (!selectedGuest) {
      toast("No guest selected", {
        description: "Please select a guest to RSVP",
        position: "top-center"
      });
      return false;
    }
    
    // Validate phone number before proceeding (only for new RSVPs)
    if (!isUpdate && rsvpResponse === 'yes' && !phoneNumber) {
      toast("Phone number required", {
        description: "Please provide a phone number to RSVP",
        position: "top-center"
      });
      return false;
    }
    
    setIsSubmitting(true);
    
    try {
      const rsvpStatus = rsvpResponse === 'yes' ? 'accepted' : 'declined';
      
      // Use directFeedback if provided, otherwise use feedback from context
      const feedbackToSubmit = directFeedback !== undefined ? directFeedback : feedback;
      
      console.log('Submitting RSVP with data:', {
        guest: selectedGuest,
        rsvpStatus,
        dates: selectedDates,
        times: selectedTimes,
        feedback: feedbackToSubmit,
        phoneNumber,
        guestAvatarUrl,
        isUpdate
      });
      
      // Prepare feedback data for submission
      const feedbackData = {
        rsvpStatus: rsvpStatus,
        selectedDates: selectedDates,
        selectedTimes: selectedTimes,
        feedbackText: feedbackToSubmit, // Use the direct feedback value here
        phoneNumber: phoneNumber,
        guestAvatarUrl: guestAvatarUrl
      };
      
      console.log('Prepared feedback data for submission:', feedbackData);
      
      // First submit the RSVP data including the phone number
      const success = await inviteService.submitInviteFeedback(
        selectedGuest.inviteId,
        feedbackData
      );
      
      if (!success) {
        throw new Error('Failed to submit RSVP');
      }
      
      console.log('RSVP submitted successfully:', {
        guest: selectedGuest,
        rsvpStatus,
        dates: selectedDates,
        times: selectedTimes,
        feedback: feedbackToSubmit,
        guestAvatarUrl
      });
      
      // Only send notification if response is 'yes' AND it's not an update
      if (rsvpResponse === 'yes' && !isUpdate) {
        try {
          // Double check that we have a valid eventId
          if (!eventId) {
            console.error('Missing eventId for SMS notification');
            throw new Error('Missing eventId for SMS notification');
          }
          
          console.log('Attempting to send confirmation SMS for event:', eventId, 'invite:', selectedGuest.inviteId);
          
          // Add a small delay to ensure the database has processed the RSVP submission
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const smsResult = await inviteService.sendEventNotification(
            eventId,
            selectedGuest.inviteId,
            'rsvp_confirmation',
            phoneNumber // Pass phone number directly to the notification service
          );
          
          if (smsResult) {
            console.log('Confirmation SMS sent successfully');
          } else {
            console.error('Error sending confirmation SMS');
            // Don't fail the RSVP process if SMS fails
          }
        } catch (smsError) {
          console.error('Error sending confirmation SMS:', smsError);
          // Don't fail the RSVP process if SMS fails
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      setError(error instanceof Error ? error : new Error('An unknown error occurred'));
      
      toast("RSVP Submission Failed", {
        description: "There was an error submitting your RSVP. Please try again.",
        position: "top-center"
      });
      
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add the resetSubmitState function
  const resetSubmitState = () => {
    setError(null);
  };

  return {
    submitRSVP,
    isSubmitting,
    error,
    resetSubmitState
  };
};
